/**
 * Trend Analysis Agent
 * Identifies trending products, popular farms, seasonal trends
 */

interface TrendData {
  id: string;
  type: 'product' | 'farm' | 'category';
  name: string;
  timestamp: Date;
  metric: number; // views, searches, purchases, etc.
  metadata?: Record<string, any>;
}

interface Trend {
  name: string;
  type: 'product' | 'farm' | 'category';
  score: number;
  momentum: number; // rate of change
  rank: number;
  popularity: number; // 0-100
  growthRate: number; // percentage growth
  peakTiming?: string;
  trendDirection: 'rising' | 'falling' | 'stable';
}

interface SeasonalTrend {
  month: number;
  monthName: string;
  trendingItems: Trend[];
  seasonalInsight: string;
}

class TrendAnalysisAgent {
  private trendData: TrendData[] = [];

  /**
   * Record trend data
   */
  recordTrend(trend: TrendData): void {
    this.trendData.push(trend);
  }

  /**
   * Record multiple trends at once
   */
  recordTrends(trends: TrendData[]): void {
    this.trendData.push(...trends);
  }

  /**
   * Get trending products
   */
  getTrendingProducts(limit: number = 10): Trend[] {
    return this.getTrendingItems('product', limit);
  }

  /**
   * Get trending farms
   */
  getTrendingFarms(limit: number = 10): Trend[] {
    return this.getTrendingItems('farm', limit);
  }

  /**
   * Get trending categories
   */
  getTrendingCategories(limit: number = 10): Trend[] {
    return this.getTrendingItems('category', limit);
  }

  /**
   * Get trending items of a specific type
   */
  private getTrendingItems(
    type: 'product' | 'farm' | 'category',
    limit: number
  ): Trend[] {
    // Get data from last 30 days
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const recentData = this.trendData.filter(
      (d) => d.type === type && d.timestamp >= thirtyDaysAgo
    );

    if (recentData.length === 0) {
      return [];
    }

    // Group by item name and aggregate
    const aggregated: {
      [key: string]: { totalMetric: number; dataPoints: TrendData[] };
    } = {};

    recentData.forEach((d) => {
      if (!aggregated[d.name]) {
        aggregated[d.name] = { totalMetric: 0, dataPoints: [] };
      }
      aggregated[d.name].totalMetric += d.metric;
      aggregated[d.name].dataPoints.push(d);
    });

    // Calculate trends
    const trends: Trend[] = Object.entries(aggregated).map(
      ([name, data], index) => {
        const totalMetric = data.totalMetric;
        const dataPoints = data.dataPoints.sort(
          (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
        );

        // Calculate growth rate
        const growthRate = this.calculateGrowthRate(dataPoints);

        // Calculate momentum (velocity of growth)
        const momentum = this.calculateMomentum(dataPoints);

        // Calculate popularity (0-100)
        const maxMetric = Math.max(
          ...Object.values(aggregated).map((d) => d.totalMetric)
        );
        const popularity = Math.round((totalMetric / maxMetric) * 100);

        // Determine trend direction
        let trendDirection: 'rising' | 'falling' | 'stable' = 'stable';
        if (growthRate > 10) {
          trendDirection = 'rising';
        } else if (growthRate < -10) {
          trendDirection = 'falling';
        }

        return {
          name,
          type,
          score: totalMetric,
          momentum,
          rank: 0,
          popularity,
          growthRate,
          trendDirection,
        };
      }
    );

    // Sort by popularity and momentum
    trends.sort(
      (a, b) =>
        b.popularity + b.momentum * 0.5 - (a.popularity + a.momentum * 0.5)
    );

    // Assign ranks
    trends.forEach((t, i) => {
      t.rank = i + 1;
    });

    return trends.slice(0, limit);
  }

  /**
   * Calculate growth rate over time
   */
  private calculateGrowthRate(dataPoints: TrendData[]): number {
    if (dataPoints.length < 2) return 0;

    const sortedPoints = dataPoints.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    const firstValue = sortedPoints[0].metric;
    const lastValue = sortedPoints[sortedPoints.length - 1].metric;

    if (firstValue === 0) return 0;

    return ((lastValue - firstValue) / firstValue) * 100;
  }

  /**
   * Calculate momentum (acceleration of growth)
   */
  private calculateMomentum(dataPoints: TrendData[]): number {
    if (dataPoints.length < 3) return 0;

    const sortedPoints = dataPoints.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    // Calculate simple rate of change
    const firstThird = sortedPoints.slice(
      0,
      Math.floor(sortedPoints.length / 3)
    );
    const lastThird = sortedPoints.slice(
      Math.floor((sortedPoints.length * 2) / 3)
    );

    const firstAvg =
      firstThird.reduce((sum, p) => sum + p.metric, 0) / firstThird.length;
    const lastAvg =
      lastThird.reduce((sum, p) => sum + p.metric, 0) / lastThird.length;

    return lastAvg - firstAvg;
  }

  /**
   * Get seasonal trends for current month
   */
  getSeasonalTrends(): SeasonalTrend[] {
    const seasonalTrends: SeasonalTrend[] = [];
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    for (let month = 1; month <= 12; month++) {
      const monthData = this.trendData.filter(
        (d) => d.timestamp.getMonth() + 1 === month
      );

      if (monthData.length > 0) {
        const trendingItems = this.getTrendingItemsByMonth(month, 5);
        const seasonalInsight = this.getSeasonalInsight(month, trendingItems);

        seasonalTrends.push({
          month,
          monthName: monthNames[month - 1],
          trendingItems,
          seasonalInsight,
        });
      }
    }

    return seasonalTrends;
  }

  /**
   * Get trending items for a specific month
   */
  private getTrendingItemsByMonth(
    month: number,
    limit: number
  ): Trend[] {
    const monthData = this.trendData.filter(
      (d) => d.timestamp.getMonth() + 1 === month
    );

    if (monthData.length === 0) {
      return [];
    }

    const aggregated: {
      [key: string]: { totalMetric: number; type: string };
    } = {};

    monthData.forEach((d) => {
      if (!aggregated[d.name]) {
        aggregated[d.name] = { totalMetric: 0, type: d.type };
      }
      aggregated[d.name].totalMetric += d.metric;
    });

    const maxMetric = Math.max(
      ...Object.values(aggregated).map((d) => d.totalMetric)
    );

    return Object.entries(aggregated)
      .map(([name, data]) => ({
        name,
        type: data.type as 'product' | 'farm' | 'category',
        score: data.totalMetric,
        momentum: 0,
        rank: 0,
        popularity: Math.round((data.totalMetric / maxMetric) * 100),
        growthRate: 0,
        trendDirection: 'stable' as const,
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);
  }

  /**
   * Get seasonal insight text
   */
  private getSeasonalInsight(month: number, trends: Trend[]): string {
    let insight = '';

    const season =
      [12, 1, 2].includes(month)
        ? 'winter'
        : [3, 4, 5].includes(month)
          ? 'spring'
          : [6, 7, 8].includes(month)
            ? 'summer'
            : 'fall';

    if (season === 'winter') {
      insight = 'Peak season for citrus and cold-weather vegetables. Holiday shopping increases sales.';
    } else if (season === 'spring') {
      insight = 'Warm weather produces spring vegetables. Growing demand for fresh produce.';
    } else if (season === 'summer') {
      insight = 'Peak season for tropical fruits and heat-tolerant crops. Tourism increases farm visits.';
    } else {
      insight = 'Fall transition season. Farmers markets become popular gathering places.';
    }

    if (trends.length > 0) {
      insight += ` Top product: ${trends[0].name}.`;
    }

    return insight;
  }

  /**
   * Get comparison between two time periods
   */
  compareTrendPeriods(
    startDate1: Date,
    endDate1: Date,
    startDate2: Date,
    endDate2: Date
  ): {
    period1: { name: string; score: number; popularity: number }[];
    period2: { name: string; score: number; popularity: number }[];
    newTrends: string[];
    fadeingTrends: string[];
  } {
    const period1Data = this.trendData.filter(
      (d) => d.timestamp >= startDate1 && d.timestamp <= endDate1
    );
    const period2Data = this.trendData.filter(
      (d) => d.timestamp >= startDate2 && d.timestamp <= endDate2
    );

    const aggregateByName = (data: TrendData[]) => {
      const agg: { [key: string]: number } = {};
      data.forEach((d) => {
        agg[d.name] = (agg[d.name] || 0) + d.metric;
      });
      return agg;
    };

    const agg1 = aggregateByName(period1Data);
    const agg2 = aggregateByName(period2Data);

    const max1 = Math.max(...Object.values(agg1), 1);
    const max2 = Math.max(...Object.values(agg2), 1);

    const period1Items = Object.entries(agg1)
      .map(([name, score]) => ({
        name,
        score,
        popularity: Math.round((score / max1) * 100),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);

    const period2Items = Object.entries(agg2)
      .map(([name, score]) => ({
        name,
        score,
        popularity: Math.round((score / max2) * 100),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);

    const names1 = new Set(period1Items.map((i) => i.name));
    const names2 = new Set(period2Items.map((i) => i.name));

    const newTrends = Array.from(names2).filter((n) => !names1.has(n));
    const fadeingTrends = Array.from(names1).filter((n) => !names2.has(n));

    return {
      period1: period1Items,
      period2: period2Items,
      newTrends,
      fadeingTrends,
    };
  }

  /**
   * Get correlation between trends
   */
  analyzeTrendCorrelation(
    itemName1: string,
    itemName2: string
  ): {
    correlation: number;
    trend1: Trend | null;
    trend2: Trend | null;
    insight: string;
  } {
    const data1 = this.trendData.filter((d) => d.name === itemName1);
    const data2 = this.trendData.filter((d) => d.name === itemName2);

    if (data1.length === 0 || data2.length === 0) {
      return {
        correlation: 0,
        trend1: null,
        trend2: null,
        insight: 'Insufficient data for correlation analysis',
      };
    }

    // Simple correlation calculation
    const sortedData1 = data1.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
    const sortedData2 = data2.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );

    const metrics1 = sortedData1.map((d) => d.metric);
    const metrics2 = sortedData2.map((d) => d.metric);

    const minLength = Math.min(metrics1.length, metrics2.length);
    const correlation = this.calculatePearsonCorrelation(
      metrics1.slice(0, minLength),
      metrics2.slice(0, minLength)
    );

    const trend1 = this.getTrendingProducts(1).find(
      (t) => t.name === itemName1
    ) || null;
    const trend2 = this.getTrendingProducts(1).find(
      (t) => t.name === itemName2
    ) || null;

    const insight =
      correlation > 0.7
        ? `${itemName1} and ${itemName2} are strongly correlated - they may appeal to the same audience`
        : correlation < -0.7
          ? `${itemName1} and ${itemName2} show inverse correlation - they may serve different markets`
          : `${itemName1} and ${itemName2} have weak correlation`;

    return {
      correlation: Math.round(correlation * 100) / 100,
      trend1,
      trend2,
      insight,
    };
  }

  /**
   * Calculate Pearson correlation coefficient
   */
  private calculatePearsonCorrelation(
    arr1: number[],
    arr2: number[]
  ): number {
    const mean1 = arr1.reduce((a, b) => a + b) / arr1.length;
    const mean2 = arr2.reduce((a, b) => a + b) / arr2.length;

    let numerator = 0;
    let denominator1 = 0;
    let denominator2 = 0;

    for (let i = 0; i < arr1.length; i++) {
      const diff1 = arr1[i] - mean1;
      const diff2 = arr2[i] - mean2;

      numerator += diff1 * diff2;
      denominator1 += diff1 * diff1;
      denominator2 += diff2 * diff2;
    }

    const denominator = Math.sqrt(denominator1 * denominator2);
    return denominator === 0 ? 0 : numerator / denominator;
  }

  /**
   * Clear trend history
   */
  clearTrendHistory(): void {
    this.trendData = [];
  }
}

export default new TrendAnalysisAgent();
